import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { strategyUsageRecords as api } from "@/lib/api/strategy-usage-records";
import type {
  PositionCloseInput,
  PositionCloseUpdateInput,
  StrategyUsageFilter,
  StrategyUsageRecordInput,
} from "@/lib/api/strategy-usage-records";
import type {
  StrategyUsageDailyTotal,
  StrategyUsageRecord,
  StrategyUsageSummary,
} from "@/types/api";
import { useAuth } from "./useAuth";
import { useUserChannel } from "./useUserChannel";

interface StrategyUsageRecordsContextValue {
  readonly records: readonly StrategyUsageRecord[];
  readonly dailyTotals: readonly StrategyUsageDailyTotal[];
  readonly summaries: readonly StrategyUsageSummary[];
  readonly loading: boolean;
  readonly loaded: boolean;
  updateClose: (
    id: string,
    input: PositionCloseUpdateInput,
  ) => Promise<StrategyUsageRecord>;
  closePositions: (
    workspaceId: string,
    inputs: (PositionCloseInput & { strategy_id: string })[],
  ) => Promise<StrategyUsageRecord[]>;
  closePosition: (
    workspaceId: string,
    strategyId: string,
    input: PositionCloseInput,
  ) => Promise<StrategyUsageRecord>;
  readonly error: Error | null;
  reload: (filter?: StrategyUsageFilter) => Promise<void>;
  create: (input: StrategyUsageRecordInput) => Promise<StrategyUsageRecord>;
  update: (
    id: string,
    input: StrategyUsageRecordInput,
  ) => Promise<StrategyUsageRecord>;
  remove: (id: string) => Promise<void>;
}

const StrategyUsageRecordsContext =
  createContext<StrategyUsageRecordsContextValue>(null!);

export function useStrategyUsageRecords() {
  return useContext(StrategyUsageRecordsContext);
}

export function StrategyUsageRecordsProvider({
  children,
  deferInitialLoad = false,
}: {
  children: ReactNode;
  deferInitialLoad?: boolean;
}) {
  const { user } = useAuth();
  return (
    <AccountStrategyUsageRecordsProvider
      key={user?.id ?? "signed-out"}
      deferInitialLoad={deferInitialLoad}
    >
      {children}
    </AccountStrategyUsageRecordsProvider>
  );
}

function AccountStrategyUsageRecordsProvider({
  children,
  deferInitialLoad = false,
}: {
  children: ReactNode;
  deferInitialLoad?: boolean;
}) {
  const { status: authStatus } = useAuth();
  const [records, setRecords] = useState<readonly StrategyUsageRecord[]>([]);
  const [summaries, setSummaries] = useState<readonly StrategyUsageSummary[]>(
    [],
  );
  const [dailyTotals, setDailyTotals] = useState<
    readonly StrategyUsageDailyTotal[]
  >([]);
  const reloadVersion = useRef(0);
  const activeFilter = useRef<StrategyUsageFilter | undefined>(undefined);
  const channel = useUserChannel();
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const reload = useCallback(async (filter?: StrategyUsageFilter) => {
    const version = ++reloadVersion.current;
    activeFilter.current = filter;
    setLoading(true);
    setError(null);
    try {
      const [recordsResponse, summaryResponse] = await Promise.all([
        api.listStrategyUsageRecords(filter),
        api.getStrategyUsageSummary(filter),
      ]);
      const loaded = [...recordsResponse.strategy_usage_records];
      let nextPage =
        filter?.source === "general" ? recordsResponse.next_page : null;
      while (nextPage) {
        if (version !== reloadVersion.current) return;
        const response = await api.listStrategyUsageRecords(filter, nextPage);
        loaded.push(...response.strategy_usage_records);
        nextPage = response.next_page;
      }
      if (version !== reloadVersion.current) return;
      const byId = new Map(loaded.map((record) => [record.id, record]));
      setRecords((previous) => {
        const existing = new Map(previous.map((record) => [record.id, record]));
        return Array.from(byId.values(), (record) => {
          const old = existing.get(record.id);
          return old && old.updated_at === record.updated_at ? old : record;
        });
      });
      setLoaded(true);
      setSummaries(summaryResponse.strategies);
      setDailyTotals(summaryResponse.daily_totals ?? []);
    } catch (nextError) {
      if (version === reloadVersion.current) setError(nextError as Error);
    } finally {
      if (version === reloadVersion.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authStatus !== "authenticated" || deferInitialLoad) return;
    queueMicrotask(() => void reload());
  }, [authStatus, deferInitialLoad, reload]);

  useEffect(
    () =>
      channel?.subscribe((message) => {
        if (message.event === "general.position_closed")
          void reload(activeFilter.current);
      }),
    [channel, reload],
  );

  const closePositions = useCallback(
    async (
      workspaceId: string,
      inputs: (PositionCloseInput & { strategy_id: string })[],
    ) => {
      const response = await api.closePositions(workspaceId, inputs);
      const saved = response.strategy_usage_records;
      ++reloadVersion.current;
      setRecords((previous) => {
        const replacements = new Map(
          saved.map((record) => [record.id, record]),
        );
        return [
          ...saved.filter(
            (record) => !previous.some((item) => item.id === record.id),
          ),
          ...previous.map((record) => replacements.get(record.id) ?? record),
        ];
      });
      await reload(activeFilter.current);
      return saved;
    },
    [reload],
  );

  const closePosition = useCallback(
    async (
      workspaceId: string,
      strategyId: string,
      input: PositionCloseInput,
    ) => {
      const response = await api.closePosition(workspaceId, strategyId, input);
      const record = response.strategy_usage_record;
      ++reloadVersion.current;
      setRecords((previous) =>
        previous.some((item) => item.id === record.id)
          ? previous.map((item) => (item.id === record.id ? record : item))
          : [record, ...previous],
      );
      await reload(activeFilter.current);
      return record;
    },
    [reload],
  );

  const updateClose = useCallback(
    async (id: string, input: PositionCloseUpdateInput) => {
      const response = await api.updateClose(id, input);
      ++reloadVersion.current;
      setRecords((previous) =>
        previous.map((record) =>
          record.id === id ? response.strategy_usage_record : record,
        ),
      );
      await reload(activeFilter.current);
      return response.strategy_usage_record;
    },
    [reload],
  );

  const create = useCallback(async (input: StrategyUsageRecordInput) => {
    const response = await api.createStrategyUsageRecord(input);
    setRecords((previous) => [response.strategy_usage_record, ...previous]);
    // A summary refresh must not turn a successful write into a retryable failure.
    try {
      const summary = await api.getStrategyUsageSummary(activeFilter.current);
      setSummaries(summary.strategies);
      setDailyTotals(summary.daily_totals ?? []);
    } catch (nextError) {
      setError(nextError as Error);
    }
    return response.strategy_usage_record;
  }, []);

  const update = useCallback(
    async (id: string, input: StrategyUsageRecordInput) => {
      const response = await api.updateStrategyUsageRecord(id, input);
      setRecords((previous) =>
        previous.map((record) =>
          record.id === id ? response.strategy_usage_record : record,
        ),
      );
      try {
        const summary = await api.getStrategyUsageSummary(activeFilter.current);
        setSummaries(summary.strategies);
        setDailyTotals(summary.daily_totals ?? []);
      } catch (nextError) {
        setError(nextError as Error);
      }
      return response.strategy_usage_record;
    },
    [],
  );

  const remove = useCallback(async (id: string) => {
    await api.deleteStrategyUsageRecord(id);
    setRecords((previous) => previous.filter((record) => record.id !== id));
    try {
      const summary = await api.getStrategyUsageSummary(activeFilter.current);
      setSummaries(summary.strategies);
      setDailyTotals(summary.daily_totals ?? []);
    } catch (nextError) {
      setError(nextError as Error);
    }
  }, []);

  const value = useMemo(
    () => ({
      records,
      summaries,
      dailyTotals,
      loading,
      loaded,
      closePosition,
      closePositions,
      updateClose,
      error,
      reload,
      create,
      update,
      remove,
    }),
    [
      records,
      summaries,
      dailyTotals,
      loading,
      loaded,
      closePosition,
      closePositions,
      updateClose,
      error,
      reload,
      create,
      update,
      remove,
    ],
  );

  return (
    <StrategyUsageRecordsContext.Provider value={value}>
      {children}
    </StrategyUsageRecordsContext.Provider>
  );
}
